import React from 'react';
import styles from './FileItem.module.css';

export interface FileData {
  id: string;
  fileName: string;
  portfolioId: string;
  folderId: string;
  type?: string;
  areaId?: string;
  questionId?: string;
  source?: 'library' | 'uploaded';
  conversationId?: string;
  /** FileLinks document id — required to remove a stale library link. */
  linkId?: string;
  /** False when the portfolio file was deleted but the link remains. */
  isAvailable?: boolean;
}

interface FileItemProps {
  file: FileData;
  onClick?: (file: FileData) => void;
  downloading?: boolean;
  onRemove?: (file: FileData) => void;
  removing?: boolean;
}

const FileItem: React.FC<FileItemProps> = ({
  file,
  onClick,
  downloading,
  onRemove,
  removing,
}) => {
  const unavailable = file.isAvailable === false;
  const canDownload = !unavailable && onClick;

  const handleClick = () => {
    if (canDownload) {
      onClick(file);
    }
  };

  const handleRemoveClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onRemove && !removing) {
      onRemove(file);
    }
  };



  const getFileType = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return 'PDF';
      case 'doc':
      case 'docx':
        return 'Word';
      case 'xls':
      case 'xlsx':
        return 'Excel';
      case 'ppt':
      case 'pptx':
        return 'PowerPoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'Image';
      case 'mp3':
      case 'wav':
        return 'Audio';
      case 'mp4':
      case 'avi':
        return 'Video';
      case 'zip':
        return 'Archive';
      case 'txt':
        return 'Text';
      default:
        return extension?.toUpperCase() || 'File';
    }
  };

  return (
    <div
      className={`${styles.fileItem} ${downloading ? styles.downloading : ""} ${unavailable ? styles.unavailable : ""}`}
      onClick={downloading || removing ? undefined : handleClick}
      role={canDownload ? "button" : undefined}
      tabIndex={canDownload && !downloading ? 0 : undefined}
      onKeyDown={
        canDownload && !downloading
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
    >
      <div className={styles.fileInfo}>
        <p className={styles.fileName}>{file.fileName}</p>
        {unavailable && (
          <p className={styles.unavailableHint}>Removed from portfolio</p>
        )}
      </div>
      <div className={styles.fileActions}>
        {unavailable && onRemove && (
          <button
            type="button"
            className={styles.removeButton}
            onClick={handleRemoveClick}
            disabled={removing}
            aria-label={`Remove link to ${file.fileName}`}
          >
            {removing ? "Removing…" : "Remove link"}
          </button>
        )}
        <span className={`${styles.fileType} ${unavailable ? styles.fileTypeUnavailable : ""}`}>
          {unavailable ? "Unavailable" : getFileType(file.fileName)}
        </span>
      </div>
    </div>
  );
};

export default FileItem;
